// Netzfrequenz: Live-Stand aus internal/netzfrequenz (Poller, alle 10 s).
// Endpoint /v2-api/netzfrequenz ist Boot-Hydration + Reconciliation; die
// eigentlichen Werte kommen per WS "netzfrequenz" nach jeder Messung, in
// derselben Form wie der Endpoint (wie wan-summary).
//
// SWR: ja -- beim Reload steht sofort die letzte Frequenz statt "—".

import { SvelteMap } from "svelte/reactivity";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface NetzfrequenzStatus {
  hz: number | null;
  /** Abweichung vom Nennwert 50 Hz in mHz, mit Vorzeichen */
  deviation_mhz: number | null;
  measured_at: string;
  source: string;
  status: "ok" | "warning" | "problem" | "stale" | "unknown";
  since: string;
  last_check: string;
  error?: string;
  thresholds: { warn_mhz: number; crit_mhz: number; hold_s: number };
  /** letzte 24 h */
  day?: { min_hz: number; max_hz: number; avg_hz: number; samples: number };
  /** Beginn der letzten gemeldeten Abweichung */
  last_excursion?: string;
  /** Minutenmittel der letzten Stunde, aelteste zuerst */
  spark: number[];
}

const STORAGE_KEY = "netzfrequenz";
const POLL_MS = 60_000;

const _state = new SvelteMap<string, NetzfrequenzStatus>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;
let _unsubscribe: (() => void) | undefined;

function uebernimm(s: NetzfrequenzStatus): void {
  _state.set("current", s);
  persist(STORAGE_KEY, s);
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/netzfrequenz", { credentials: "include" });
    if (!r.ok) return;
    uebernimm((await r.json()) as NetzfrequenzStatus);
  } catch {
    // ignore -- naechster Frame/Tick holt es nach
  }
}

export function startNetzfrequenz(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  const cached = loadPersisted<NetzfrequenzStatus>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
  void refresh();
  // Nur Reconciliation: die Werte kommen alle 10 s per WS.
  _timer = setInterval(refresh, POLL_MS);
  _unsubscribe = wsSubscribe("netzfrequenz", (d) => {
    const s = d as NetzfrequenzStatus;
    if (!s || typeof s.status !== "string") return;
    uebernimm(s);
  });
}

export function stopNetzfrequenz(): void {
  _started = false;
  if (_timer) {
    clearInterval(_timer);
    _timer = undefined;
  }
  _unsubscribe?.();
  _unsubscribe = undefined;
  _state.clear();
}

export function getNetzfrequenz(): NetzfrequenzStatus | undefined {
  return _state.get("current");
}

/** hzText: 49.994 -> "49,994". Drei Nachkommastellen: die Quelle loest in
 *  mHz auf, und genau dort spielt sich der Alltag ab. */
export function hzText(hz: number | null | undefined): string {
  return hz == null ? "—" : hz.toLocaleString("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** mhzText: -6.2 -> "−6 mHz", 12 -> "+12 mHz". */
export function mhzText(mhz: number | null | undefined): string {
  if (mhz == null) return "—";
  const v = Math.round(mhz);
  return `${v > 0 ? "+" : v < 0 ? "−" : "±"}${Math.abs(v)} mHz`;
}
